import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, QrCode, Zap, ShieldCheck, Upload, CreditCard, PrinterCheck, MonitorSmartphone, Wallet } from "lucide-react";
import HeroDemo from "@/pages/HeroDemo";

const steps = [
  { icon: QrCode, title: "Scan the counter QR", body: "Customers open a mobile-friendly page in one tap. No app, no signup." },
  { icon: Upload, title: "Upload & configure", body: "Drop PDFs / DOCX / images. Pick B&W or colour, duplex, copies, orientation." },
  { icon: CreditCard, title: "Pay online — or at the counter", body: "One-tap UPI / cards / netbanking via Razorpay, or choose Pay-at-counter and hand over cash to the shop." },
  { icon: PrinterCheck, title: "Prints automatically", body: "Your Print Agent picks the paid job and sends it to the shop printer silently." },
];

const features = [
  { icon: MonitorSmartphone, title: "Multi-tenant SaaS", body: "Every shop gets its own subdomain, pricing, printer and analytics." },
  { icon: Wallet, title: "Cash-first, online-optional", body: "New shops accept Pay-at-counter by default. Enable Razorpay whenever you're ready — every rupee lands in your own account." },
  { icon: ShieldCheck, title: "Tenant-safe & audit-logged", body: "Every action tenant-scoped. Every significant event tracked in audit logs." },
  { icon: Zap, title: "Real Print Agent", body: "One-click Windows installer, silent SumatraPDF, LibreOffice office conversion, multi-printer." },
];

export default function Landing() {
  return (
    <div className="min-h-screen paper-bg overflow-x-hidden">
      <header className="max-w-6xl mx-auto flex items-center justify-between gap-2 px-4 sm:px-6 py-4 sm:py-5">
        <Link to="/" className="flex items-center gap-2 min-w-0 shrink-0">
          <img src="/logo.png" alt="Print Plus" className="w-9 h-9 sm:w-10 sm:h-10 object-contain shrink-0" />
          <div className="leading-tight whitespace-nowrap">
            <div className="font-display text-base sm:text-xl font-bold">Print Plus</div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-orange-500 font-semibold -mt-0.5">by AI Upaya</div>
          </div>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <Link to="/track" className="hidden md:inline-flex"><Button variant="ghost" data-testid="nav-track-order">Track Order</Button></Link>
          <Link to="/get-started" className="hidden sm:inline-flex"><Button variant="ghost" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 font-semibold" data-testid="nav-try-free">Try free forever</Button></Link>
          <Link to="/get-started" className="sm:hidden"><Button size="sm" variant="ghost" className="h-9 px-2.5 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 font-semibold" data-testid="nav-try-free-mobile">Try free</Button></Link>
          <Link to="/login"><Button size="sm" className="h-9 px-3 sm:h-10 sm:px-4 bg-orange-500 hover:bg-orange-600" data-testid="nav-login">Sign in</Button></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-14 sm:pb-16 grid md:grid-cols-2 gap-10 md:gap-14 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs sm:text-sm text-orange-700 font-medium mb-5 sm:mb-6">
            <Zap className="w-3.5 h-3.5" /> Multi-tenant SaaS for print shops
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-black tracking-tighter leading-[1.05] mb-5 sm:mb-6">
            Turn any Xerox shop into a <span className="text-orange-500">scan-to-print</span> counter.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mb-7 sm:mb-8">
            Customers scan a QR, upload files, pay, and pick up. You manage pricing, printers, jobs and revenue from one clean dashboard.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/get-started"><Button size="lg" className="bg-orange-500 hover:bg-orange-600 h-12 px-6 text-base" data-testid="cta-freetrial">Start free · 10/day</Button></Link>
            <Link to="/s/demo-store"><Button size="lg" variant="outline" className="h-12 px-6 text-base" data-testid="cta-demo">Try demo shop</Button></Link>
          </div>
          <div className="mt-7 sm:mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />No app required</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-orange-500" />Razorpay + UPI</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Pay at counter (cash)</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />Multi-printer</span>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-200/40 via-amber-100/40 to-transparent blur-2xl -z-10" />
          <HeroDemo />
          <div className="mt-4 grid grid-cols-3 gap-3 text-center max-w-[320px] mx-auto">
            <div className="rounded-xl bg-white border border-slate-200 p-2.5"><Wallet className="w-4 h-4 mx-auto text-amber-500 mb-1" /><div className="text-[11px] text-slate-500">Cash-first</div></div>
            <div className="rounded-xl bg-white border border-slate-200 p-2.5"><Zap className="w-4 h-4 mx-auto text-orange-500 mb-1" /><div className="text-[11px] text-slate-500">Instant</div></div>
            <div className="rounded-xl bg-white border border-slate-200 p-2.5"><ShieldCheck className="w-4 h-4 mx-auto text-emerald-500 mb-1" /><div className="text-[11px] text-slate-500">Secure</div></div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 md:py-20">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-2">How it works</div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-black tracking-tight">From counter QR to printed page in under 60 seconds</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div key={s.title} className="relative rounded-2xl bg-white border border-slate-200 p-5 sm:p-6 hover:-translate-y-0.5 hover:shadow-md transition">
              <div className="text-[10px] font-mono text-slate-400 mb-3">STEP {String(i + 1).padStart(2, "0")}</div>
              <div className="w-10 h-10 rounded-xl bg-orange-100 grid place-items-center mb-3"><s.icon className="w-5 h-5 text-orange-600" /></div>
              <div className="font-display font-bold mb-1">{s.title}</div>
              <div className="text-sm text-slate-500 leading-relaxed">{s.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 sm:pb-16 md:pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
              <div className="w-10 h-10 rounded-xl bg-slate-900 grid place-items-center mb-3"><f.icon className="w-5 h-5 text-orange-400" /></div>
              <div className="font-display font-bold mb-1">{f.title}</div>
              <div className="text-sm text-slate-500 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6 sm:p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-orange-500/30 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-display font-black tracking-tight mb-3">Ready to modernise your Xerox counter?</h3>
              <p className="text-white/70 max-w-md text-sm sm:text-base">Set up in minutes. Onboard customers with a single QR code. No app to install.</p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/login"><Button size="lg" className="bg-orange-500 hover:bg-orange-600 h-12 px-6 text-base">Open dashboard</Button></Link>
              <Link to="/s/demo-store"><Button size="lg" variant="outline" className="h-12 px-6 text-base bg-transparent border-white/25 hover:bg-white/10 text-white">Try demo</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 text-xs text-slate-500 flex flex-wrap gap-3 justify-between">
        <div>© 2026 Print Plus by AI Upaya</div>
        <div className="flex gap-4"><Link to="/track" className="hover:text-orange-600">Track order</Link><Link to="/login" className="hover:text-orange-600">Sign in</Link></div>
      </footer>
    </div>
  );
}
